// ----------------------------------------------------------------------
// Shared localStorage key helpers — used by useSubscription, PlanSelectionModal, and callback pages
// ----------------------------------------------------------------------

export const optimisticCancelKey = (id: string) => `__optimistic_cancel_${id}`;

export const pendingPlanKey = (id: string) => `__pending_billing_plan_${id}`;

export const CANCEL_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

export const PENDING_PLAN_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

export function readPendingPlan(orgId: string): string | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(pendingPlanKey(orgId));
  if (!raw) return null;
  try {
    const { plan, ts } = JSON.parse(raw) as { plan: string; ts: number };
    if (Date.now() - ts < PENDING_PLAN_EXPIRY_MS) return plan;
    localStorage.removeItem(pendingPlanKey(orgId));
  } catch {
    localStorage.removeItem(pendingPlanKey(orgId));
  }
  return null;
}

// ----------------------------------------------------------------------

export type BillingState = 'free' | 'active' | 'cancelled' | 'expired';

export type SubscriptionWarning =
  | 'past_due'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused'
  | 'trialing'
  | 'card_expiring'
  | null;

export type BillingBannerVariant = 'upgrade' | 'reactivate';

export type SubscriptionPlan = {
  name: string;
  description: string;
  price: number;
  interval: 'month' | 'year';
  features?: string[];
};

export type SubscriptionData = {
  status: BillingState;
  plan?: SubscriptionPlan;
  nextPaymentDate?: string;
  expiryDate?: string;
  cancelledAt?: string;
};

export type Invoice = {
  id: string;
  date: string;
  total: number;
  currency: string;
  status: 'paid' | 'failed' | 'pending' | 'refunded';
  viewUrl?: string;
  hostedInvoiceUrl?: string;
};

export type PaymentMethod = {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
};

// Normalized subscription shape — useSubscription maps API fields (dateCurrentPeriodStart etc.)
// to these canonical camelCase names so components don't need to handle API field variations
export type RawSubscription = {
  planSlug?: string;
  status?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  dateCanceled?: string;
  isInTrial: boolean;
  trialStart: string | null;   // ISO datetime
  trialEnd: string | null;     // ISO datetime
};

// ----------------------------------------------------------------------
// Plan access / feature gating types
// ----------------------------------------------------------------------

export type FeatureKey =
  | 'candidates'
  | 'screenings'
  | 'interviews'
  | 'coding_rounds'
  | 'job_posts'
  | 'ai_interviews'
  | 'custom_branding'
  | 'remove_watermark'
  | 'automations'
  | 'reports';

export type PlanLimits = {
  maxCandidates: number;
  maxScreenings: number;
  maxInterviews: number;
  maxCodingRounds: number;
  maxJobPosts: number;
  hasAiInterviews: boolean;
  hasCustomBranding: boolean;
  hasRemoveWatermark: boolean;
  hasAutomations: boolean;
  hasReports: boolean;
  // Integrations page (dev accounts and Professional) — derived server-side from the plan
  hasIntegrations: boolean;
  // Workflow interview gating — returned by /api/billing/org-status planLimits
  allowedAiAgents: string[];    // e.g. ["ARIA"] or ["ARIA", "MAX"]
  allowedDurations: number[];   // e.g. [15, 30] or [15, 30, 45, 60]
};

// ----------------------------------------------------------------------
// Interview options API types
// ----------------------------------------------------------------------

export type InterviewOptionsPlan = {
  planSlug: string;
  displayName: string;
  monthlyPriceCents: number;
  allowedAiAgents: string[];
  allowedDurations: number[];
};

export type InterviewOptionsResponse = {
  currentPlan: string;
  allowedAiAgents: string[];
  allowedDurations: number[];
  allPlans: InterviewOptionsPlan[];
};

export type UsageSummary = {
  candidates: number;
  screenings: number;
  interviews: number;
  codingRounds: number;
  jobPosts: number;
  periodStart: string;
  periodEnd: string;
};

export type PlanWarning = {
  feature: string;
  featureLabel: string;
  used: number;
  limit: number;
  percentage: number;
  message: string;
};

export type PlanLimitError = {
  code: 'PLAN_LIMIT_EXCEEDED' | 'FEATURE_NOT_AVAILABLE';
  feature: string;
  featureLabel: string;
  limit?: number;
  used?: number;
  remaining?: number;
  currentPlan: string;
  upgradeTo?: string;
  requiredPlan?: string;
  message: string;
  canBuyPack: boolean;
};

export const isPlanLimitError = (
  err: unknown
): err is { response: { data: PlanLimitError } } => {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return d.code === 'PLAN_LIMIT_EXCEEDED' || d.code === 'FEATURE_NOT_AVAILABLE';
};

export const extractPlanLimitError = (err: unknown): PlanLimitError | null => {
  if (!isPlanLimitError(err)) return null;
  const raw = err.response.data as Record<string, unknown>;
  return {
    code: raw.code as PlanLimitError['code'],
    feature: String(raw.feature ?? ''),
    featureLabel: String(raw.featureLabel ?? raw.FeatureLabel ?? ''),
    limit: raw.limit != null ? Number(raw.limit) : undefined,
    used: raw.used != null ? Number(raw.used) : undefined,
    remaining: raw.remaining != null ? Number(raw.remaining) : undefined,
    currentPlan: String(raw.currentPlan ?? raw.CurrentPlan ?? ''),
    upgradeTo: raw.upgradeTo != null ? String(raw.upgradeTo) : undefined,
    requiredPlan: raw.requiredPlan != null ? String(raw.requiredPlan) : undefined,
    message: String(raw.message ?? raw.Message ?? ''),
    canBuyPack: Boolean(raw.canBuyPack ?? raw.CanBuyPack ?? false),
  };
};

export type AccessCheckResult = {
  allowed: boolean;
  limit: number;
  used: number;
  remaining: number;
  percentage: number;
  isApproachingLimit: boolean;
  warningMessage?: string;
};

// ── Credit types (Phase 4 — per-unit credits) ─────────────────────────────

export type CreditBalance = {
  feature: string;          // 'interviews' | 'screenings'
  remaining: number;
  lifetimeGranted: number;
  lifetimeConsumed: number;
  updatedAt: string;        // ISO 8601
};

export type CreditFeaturePricing = {
  feature: 'screenings' | 'interviews';
  pricePerUnitUsdCents: number;
  displayName: string;
  subtitle: string;
  unitLabel: string;
};

export type CreditPricing = {
  screening: CreditFeaturePricing | null;
  interviews: CreditFeaturePricing | null;
  purchaseIncrementUnits: number;
  minPurchaseUnits: number;
  maxPurchaseUnits: number;
  currencyCode: string;
};

// Single source of truth for credit-purchasable features — mirrors backend
// PackEnabledFeatures. The slug `interviews` is internal; the user-facing
// label is "Core Interviews" (see FEATURE_LABELS in usePlanAccess).
export const PACK_ENABLED_FEATURES = ['interviews', 'screenings'] as const;
export type PackEnabledFeature = typeof PACK_ENABLED_FEATURES[number];

export const isPackEnabledFeature = (f: string): f is PackEnabledFeature =>
  (PACK_ENABLED_FEATURES as readonly string[]).includes(f);

// ── Pack-purchase error narrowing ──────────────────────────────────────────
// Mirrors the backend's pack-purchase error codes so the modal's switch-on-code
// is compile-time exhaustive instead of relying on hand-typed string literals.

export const PACK_PURCHASE_ERROR_CODES = [
  'PACK_PURCHASE_REQUIRES_PAID_PLAN',
  'PACK_PURCHASE_BLOCKED_DURING_TRIAL',
  'PACK_PURCHASE_IN_PROGRESS',
  'INVALID_CREDIT_QUANTITY',
] as const;

export type PackPurchaseErrorCode = typeof PACK_PURCHASE_ERROR_CODES[number];

const isPackPurchaseErrorCode = (v: unknown): v is PackPurchaseErrorCode =>
  typeof v === 'string' && (PACK_PURCHASE_ERROR_CODES as readonly string[]).includes(v);

export type PackPurchaseErrorInfo = {
  /** Recognized backend code, or null when the error doesn't match the union. */
  code: PackPurchaseErrorCode | null;
  message: string;
};

/**
 * Narrows an arbitrary thrown value into `{ code, message }` for the
 * CreditPurchaseModal switch table. Code is `null` for network / 5xx /
 * unknown payload shapes; the modal falls through to its default branch.
 */
export const extractPackPurchaseError = (err: unknown): PackPurchaseErrorInfo => {
  const data = (err as { response?: { data?: unknown } } | undefined)?.response?.data as
    | Record<string, unknown>
    | undefined;
  const rawCode = data?.code ?? data?.Code;
  const code = isPackPurchaseErrorCode(rawCode) ? rawCode : null;
  const message =
    (data?.message as string | undefined) ||
    (data?.Message as string | undefined) ||
    (err as { message?: string } | undefined)?.message ||
    'Something went wrong. Please try again.';
  return { code, message };
};

// ── Bulk-send recovery types (PackRecoveryToast) ─────────────────────────
// `BulkRecoveryFeatureNoun` drives copy in PackRecoveryToast — kept as a
// display-layer noun (singular form) so the toast can pluralize for `unsentCount`.
// `'send'` is the umbrella fallback when failures span multiple features.

export type BulkRecoveryFeatureNoun = 'interview' | 'screening' | 'send';

/** View-model passed from useBulkSendWithRecovery → showPackRecoveryToast. */
export type SuggestedCreditTopUp = {
  displayName: string;
  priceUsdCents: number;
};
